/**
 * Gestion des domaines personnalisés (Cloudflare for SaaS / Custom Hostnames).
 *
 * ── Principe ─────────────────────────────────────────────────
 * Le commerçant possède déjà un sous-domaine gratuit : username.zifek.fr.
 * Il peut y ajouter SON domaine (www.maboutique.com) via un simple CNAME :
 *
 *     Type : CNAME
 *     Nom  : www
 *     Cible: domains.zifek.fr      <-- Fallback Origin Cloudflare
 *
 * Zifek crée alors automatiquement le Custom Hostname Cloudflare, valide le
 * SSL, et associe le domaine au bon marchand.
 *
 * ⚠️ SÉCURITÉ : aucun appel Cloudflare direct n'est fait ici. Tout passe par
 * l'Edge Function « cloudflare-domain » (le token Cloudflare reste serveur).
 */

import { supabase } from '@/lib/supabase';
import { APP_DOMAIN } from '@/lib/domain';

/** Fallback Origin Cloudflare utilisé pour tous les domaines personnalisés. */
export const DNS_CNAME_TARGET = 'domains.zifek.fr';

/** Domaines système de la plateforme : interdits comme domaine personnalisé. */
const RESERVED_HOSTNAMES = [APP_DOMAIN, `www.${APP_DOMAIN}`, DNS_CNAME_TARGET];

/** Normalise une saisie utilisateur en hostname propre.
 *  https://WWW.MaBoutique.com/shop/  →  www.maboutique.com
 */
export function normalizeHostname(raw: string): string {
  let h = (raw || '').trim().toLowerCase();
  h = h.replace(/^https?:\/\//, '');
  h = h.split('/')[0];
  h = h.split('?')[0];
  h = h.split('#')[0];
  h = h.replace(/\.+$/, '');
  return h.trim();
}

const HOST_RE = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

export function isReservedHostname(host: string): boolean {
  return RESERVED_HOSTNAMES.some((r) => host === r || host.endsWith(`.${r}`));
}

/** Vérifie qu'un hostname est valide et non réservé. Retourne un message d'erreur ou null. */
export function validateCustomHostname(raw: string): string | null {
  const host = normalizeHostname(raw);
  if (!host) return 'Veuillez saisir un nom de domaine.';
  if (host.includes('..') || host.startsWith('-') || host.length > 253) return 'Nom de domaine invalide.';
  if (!HOST_RE.test(host)) return 'Format de domaine invalide. Exemple : www.maboutique.com';
  const reserved = RESERVED_HOSTNAMES.some((r) => host === r || host.endsWith(`.${r}`));
  if (reserved) return 'Ce domaine appartient à la plateforme Zifek et ne peut pas être utilisé.';
  return null;
}

/** Un domaine apex saisi seul (maboutique.com) — on recommande www. */
export function isApexDomain(host: string): boolean {
  const h = normalizeHostname(host);
  return /^[a-z0-9-]+\.[a-z]{2,}$/.test(h);
}

// ── Statuts lisibles ──────────────────────────────────────────

export type DomainState =
  | 'not_configured'
  | 'pending_dns'
  | 'verifying'
  | 'ssl_pending'
  | 'active'
  | 'error';

export interface DomainStatusInfo {
  state: DomainState;
  label: string;
  tone: 'neutral' | 'warn' | 'info' | 'success' | 'danger';
  icon: string;
}

/** Traduit les statuts Cloudflare (hostname_status + ssl_status) en état lisible. */
export function resolveDomainState(
  hostnameStatus: string | null | undefined,
  sslStatus: string | null | undefined,
  isActive: boolean,
): DomainStatusInfo {
  const hs = (hostnameStatus || '').toLowerCase();
  const ss = (sslStatus || '').toLowerCase();

  if (!hs || hs === 'not_configured') {
    return { state: 'not_configured', label: 'Non configuré', tone: 'neutral', icon: 'ri-link-unlink-m' };
  }
  if (hs === 'active' && ss === 'active' && isActive) {
    return { state: 'active', label: 'Actif', tone: 'success', icon: 'ri-shield-check-line' };
  }
  if (hs === 'active' && (ss === 'pending' || ss === 'initializing' || ss === 'validation_timed_out')) {
    return { state: 'ssl_pending', label: 'SSL en cours', tone: 'info', icon: 'ri-lock-line' };
  }
  if (hs === 'pending' || hs === 'pending_validation' || hs === 'pending_issuance' || hs === 'initializing') {
    return { state: 'pending_dns', label: 'En attente DNS', tone: 'warn', icon: 'ri-time-line' };
  }
  if (hs === 'blocked' || hs === 'moved' || hs === 'deleted' || ss === 'deleted') {
    return { state: 'error', label: 'Erreur', tone: 'danger', icon: 'ri-error-warning-line' };
  }
  return { state: 'verifying', label: 'Vérification en cours', tone: 'info', icon: 'ri-radar-line' };
}

// ── Appels Edge Function ──────────────────────────────────────

export interface CustomDomainRow {
  id: number;
  user_id: number | null;
  domaine: string | null;
  verified: boolean;
  cloudflare_custom_hostname_id: string | null;
  hostname_status: string | null;
  ssl_status: string | null;
  is_active: boolean;
  provider: string | null;
  error_message: string | null;
  last_checked_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface EdgeResult {
  error?: string;
  hostname?: string;
  hostname_status?: string;
  ssl_status?: string;
  is_active?: boolean;
  success?: boolean;
  ok?: boolean;
  configured?: boolean;
  total?: number;
  active?: number;
  expired?: number;
  domains?: Array<Record<string, unknown>>;
  results?: Array<{
    id: number;
    domaine: string | null;
    user_id?: number | null;
    hostname_status?: string | null;
    ssl_status?: string | null;
    is_active?: boolean;
    expired?: boolean;
    error?: string;
  }>;
}

/** Lit la ligne de domaine personnalisé d'un marchand. */
export async function fetchMyCustomDomain(userId: number): Promise<CustomDomainRow | null> {
  const { data } = await supabase
    .from('websitedomain')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  return (data as CustomDomainRow) ?? null;
}

/**
 * Hostname du domaine personnalisé ACTIF d'un marchand (ou null).
 * Utilisé pour construire les liens publics (ex : « Ma boutique publique » d'un
 * vendeur) sur le domaine propre de la marketplace plutôt que son sous-domaine.
 */
export async function fetchActiveCustomDomain(userId: number): Promise<string | null> {
  if (!userId || !Number.isFinite(userId)) return null;
  try {
    const row = await fetchMyCustomDomain(userId);
    if (!row || !row.domaine) return null;
    const info = resolveDomainState(row.hostname_status, row.ssl_status, row.is_active);
    if (info.state !== 'active') return null;
    return normalizeHostname(row.domaine);
  } catch {
    return null;
  }
}

async function invokeDomain(body: Record<string, unknown>): Promise<EdgeResult> {
  try {
    const { data, error } = await supabase.functions.invoke('cloudflare-domain', { body });
    if (error) return { error: error.message || 'Erreur de communication avec le serveur.' };
    return (data ?? {}) as EdgeResult;
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Erreur inconnue';
    return { error: msg };
  }
}

/** Connecte (crée) le domaine personnalisé du marchand côté Cloudflare. */
export function connectCustomDomain(params: {
  userId: number;
  userEmail: string;
  hostname: string;
}): Promise<EdgeResult> {
  return invokeDomain({
    action: 'create',
    user_id: params.userId,
    user_email: params.userEmail,
    hostname: params.hostname,
  });
}

/** Vérifie le statut du domaine personnalisé auprès de Cloudflare. */
export function checkCustomDomain(params: { userId: number; userEmail: string }): Promise<EdgeResult> {
  return invokeDomain({ action: 'check', user_id: params.userId, user_email: params.userEmail });
}

/** Déconnecte (supprime) le domaine personnalisé du marchand. */
export function disconnectCustomDomain(params: { userId: number; userEmail: string }): Promise<EdgeResult> {
  return invokeDomain({ action: 'delete', user_id: params.userId, user_email: params.userEmail });
}

/** Teste la connexion Cloudflare (Super Admin). */
export function testCloudflareConnection(params: { userId: number; userEmail: string }): Promise<EdgeResult> {
  return invokeDomain({ action: 'test', user_id: params.userId, user_email: params.userEmail });
}

/** Liste tous les domaines personnalisés (Super Admin). */
export function listCustomDomains(params: { userId: number; userEmail: string }): Promise<EdgeResult> {
  return invokeDomain({ action: 'list', user_id: params.userId, user_email: params.userEmail });
}

/** Rafraîchit le statut d'un domaine précis auprès de Cloudflare (Super Admin / propriétaire). */
export function refreshCustomDomain(params: {
  userId: number;
  userEmail: string;
  domainId: number;
}): Promise<EdgeResult> {
  return invokeDomain({
    action: 'refresh',
    user_id: params.userId,
    user_email: params.userEmail,
    domain_id: params.domainId,
  });
}

/** Vérifie EN UNE FOIS tous les domaines personnalisés (Super Admin — tâche périodique). */
export function refreshAllCustomDomains(params: { userId: number; userEmail: string }): Promise<EdgeResult> {
  return invokeDomain({ action: 'refresh_all', user_id: params.userId, user_email: params.userEmail });
}